import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { type OrderIngredient } from '@hungryroot/orders';
import {
  type Cart,
  type CartProduct,
  type OrderDetail,
  type PairingBundle,
  REACT_QUERY_KEYS,
} from '@hungryroot/rest';
import { getCartFromCache } from '@hungryroot/rest';
import {
  GET_CUSTOMER_ORDERS_QUERY_KEY,
  invalidateOrders,
  invalidateOrdersV3,
  useNextOrderV3,
} from '@hungryroot/rest-hooks';
import {
  segmentAnalytics,
  pairingInteraction,
  productInteraction,
  type AccessedFrom,
  type Location,
} from '@hungryroot/segment';
import {
  convertProductToCartProduct,
  convertPairingToCartPairing,
  convertOrderIngredientToCartProduct,
} from '../../transforms';
import { MutationTypes } from '../../types/MutationTypes';
import { type RemoveIngredientMutation } from '../../types/RemoveIngredientMutation';
import { type UseCartMutations } from '../../types/UseCartMutations';
import {
  getBundleDiffBetweenCarts,
  getCartPairingBundle,
  getCartPairingBundles,
  getCartProductBundle,
  getCartProductBundles,
  getLatestCartPairingBundle,
  getPairingCount,
  getProductCount,
} from '../../utils/query';
import { updateCart } from '../../utils/updateCart';
import { CartChangeType, executeChangeHandlers } from '../useCartEvents';
import {
  useCartMutationStateMachine,
  type MutationStateMachine,
} from './mutation-state';
import {
  addOptimisticCartProduct,
  addPairingOptimisticCart,
  bundleIsOptimistic,
} from './optimistic';
import { useAddPairingSelf } from './useAddPairingSelf';
import { useAddProductSelf } from './useAddProductSelf';
import { useDeleteBundleSelf } from './useDeleteBundleSelf';
import { useDeleteCartProductSelf } from './useDeleteCartProductSelf';
import { useErrorMessageToasts } from './useErrorMessageToasts';
import { usePatchBundleSelf } from './usePatchBundleSelf';

type RecipeRemoval = Omit<
  Parameters<UseCartMutations['removeRecipeFromBundle']>[0],
  'bundleId'
>;
type ProductRemoval = Omit<
  Parameters<UseCartMutations['removeProductFromBundle']>[0],
  'bundleId'
>;

/**
 * How many of an item the customer's clicks asked the cart to hold. Each
 * decrement lowers it and each add raises it, so it captures the intent rather
 * than the bundle a click happened to land on — a bundle id isn't knowable yet
 * while the add that creates it is still in flight.
 *
 * A target outlives failed mutations on purpose: `reconcileDesiredCounts`
 * re-runs after every cart refresh and keeps deleting the surplus until the
 * server agrees, so no click is ever silently dropped.
 */
type DesiredCount = { attempts: number; count: number } & (
  | { kind: 'pairing'; removal: RecipeRemoval }
  | { kind: 'product'; removal: ProductRemoval }
);

/**
 * Module-level for the same reason the mutation state machine is: every
 * `useCartMutations` instance acts on the one shared cart.
 */
const desiredCounts = new Map<string, DesiredCount>();

/**
 * Bundles with a delete already on the wire. Taps can outrun the cache write
 * that removes a bundle, so without this two clicks can pick the same bundle —
 * the second request would 404 and toast an error the customer didn't earn.
 * Skipping is safe: the desired count still holds the intent.
 */
const inFlightBundleDeletes = new Set<number>();

/** Consumers currently mounted, so targets never outlive the cart UI. */
let activeConsumers = 0;

/** Reconcile passes to attempt before giving up and telling the customer. */
const MAX_RECONCILE_ATTEMPTS = 3;

function desiredCountKey(kind: DesiredCount['kind'], itemId: number) {
  return `${kind}:${itemId}`;
}

/**
 * Bundle ids the server has confirmed, newest first. Optimistic bundles are
 * excluded: they carry placeholder negative ids the API would reject.
 */
function settledPairingBundleIds(cart: Cart, pairingId: number): number[] {
  return getCartPairingBundles(cart)
    .filter((bundle) => {
      return bundle.pairing.id === pairingId && !bundleIsOptimistic(bundle);
    })
    .map((bundle) => bundle.id)
    .sort((a, b) => b - a);
}

function cartHasBundle(cart: Cart, bundleId: number) {
  return cart.bundles.some((bundle) => bundle.id === bundleId);
}

/**
 * Which bundle a decrement should actually act on. The caller derives its id
 * from a render that can predate the cart it's acting on: a fast second tap
 * often names the bundle already on its way out, and a removal chained onto an
 * unskip names a bundle the unskip's shuffle deleted. Falling back to another
 * unit of the same recipe keeps every tap moving the count instead of firing a
 * DELETE that would 404.
 */
function resolvePairingBundleToRemove(
  cart: Cart,
  pairingId: number,
  requestedBundleId: number
): number | null {
  if (
    cartHasBundle(cart, requestedBundleId) &&
    !inFlightBundleDeletes.has(requestedBundleId)
  ) {
    return requestedBundleId;
  }

  const bundles = getCartPairingBundles(cart).filter((bundle) => {
    return bundle.pairing.id === pairingId;
  });
  // Prefer another saved bundle so the DELETE can go out now; an optimistic one
  // still moves the count and leaves the removal to the reconciler.
  const settled = bundles
    .filter((bundle) => {
      return (
        !bundleIsOptimistic(bundle) && !inFlightBundleDeletes.has(bundle.id)
      );
    })
    .map((bundle) => bundle.id)
    .sort((a, b) => b - a);
  if (settled.length > 0) return settled[0];

  const optimistic = bundles
    .filter(bundleIsOptimistic)
    .map((bundle) => bundle.id)
    .sort((a, b) => b - a);
  return optimistic[0] ?? null;
}

/**
 * One entry per confirmed unit of a product, newest bundle first. A bundle
 * holding the product twice appears twice, because each removal drops a single
 * order detail from it.
 */
function settledProductBundleIds(cart: Cart, productId: number): number[] {
  const bundleIds: number[] = [];
  getCartProductBundles(cart).forEach((bundle) => {
    if (bundleIsOptimistic(bundle)) return;
    bundle.order_details.forEach(({ product }) => {
      if (product === productId) bundleIds.push(bundle.id);
    });
  });
  return bundleIds.sort((a, b) => b - a);
}

/**
 * The product-removal counterpart of `resolvePairingBundleToRemove`, for a
 * bundle id that's no longer in the cart.
 */
function resolveProductBundleToRemove(
  cart: Cart,
  removal: ProductRemoval,
  requestedBundleId: number
): number | null {
  if (cartHasBundle(cart, requestedBundleId)) return requestedBundleId;
  // An ingredient edit targets one recipe; never take it from another bundle.
  if (removal.isRecipeEdit) return null;
  return settledProductBundleIds(cart, removal.productId)[0] ?? null;
}

function onMutate(queryClient: QueryClient, fsm: MutationStateMachine) {
  fsm.recordMutationStarted();
  queryClient.cancelQueries({ queryKey: [REACT_QUERY_KEYS.CART] });

  return getCartFromCache(queryClient);
}

async function onMutationSettled(
  queryClient: QueryClient,
  fsm: MutationStateMachine,
  cart: Cart | undefined,
  reconcileDesiredCounts: () => void
) {
  const state = fsm.recordMutationEnded();
  const { mutationsInFlight, refreshCart } = state;
  if (mutationsInFlight > 0) {
    // There are still mutations going, don't update the cache
    return;
  }

  queryClient.invalidateQueries({
    queryKey: GET_CUSTOMER_ORDERS_QUERY_KEY,
  });
  invalidateOrders(queryClient);
  invalidateOrdersV3(queryClient, { time_bucket: 'upcoming' });

  if (refreshCart === false) {
    if (cart !== undefined) {
      // it's safe to use the HTTP response for the cart, no possibility of conflict
      updateCart(queryClient, cart);
      reconcileDesiredCounts();
      return;
    }
    // An error response settled and the cart has been rolled back. Refetch
    // before reconciling: the rollback snapshot is a client guess, and the
    // customer's requested counts outlive a failed mutation.
    try {
      await queryClient.fetchQuery({ queryKey: [REACT_QUERY_KEYS.CART] });
    } catch {}
    reconcileDesiredCounts();
    return;
  }

  // Invalidate the cart, there were multiple mutations in flight
  // We have to just refresh the whole thing.
  try {
    /**
     * The try/catch attempts to handle an unhandled rejection of `Error: Missing queryFn: '["Cart"]'` in tests.
     * This GH issue is the most relevant: https://github.com/TanStack/query/issues/2096#issuecomment-880547144
     */
    await queryClient.fetchQuery({ queryKey: [REACT_QUERY_KEYS.CART] });
  } catch {}

  reconcileDesiredCounts();
}

function onMutationError(
  queryClient: QueryClient,
  context: { previousCart: Cart | null } | undefined
) {
  if (context === undefined || context.previousCart === null) {
    return;
  }
  const { previousCart } = context;
  updateCart(queryClient, previousCart);
}

export function useCartMutations(): UseCartMutations {
  const client = useQueryClient();
  const handleErrorWithToast = useErrorMessageToasts();
  const { order } = useNextOrderV3();

  useEffect(() => {
    activeConsumers += 1;
    return () => {
      activeConsumers -= 1;
      if (activeConsumers === 0) {
        // Nothing is left to reconcile against — don't let a target survive to
        // act on a cart the customer comes back to later.
        desiredCounts.clear();
        inFlightBundleDeletes.clear();
      }
    };
  }, []);

  const cartMutationState = useCartMutationStateMachine();
  const addProductMutation = useAddProductSelf({
    onMutate(params: {
      product: CartProduct;
      location?: Location | null;
      pageName?: string;
      conversationId?: string;
      searchId?: string;
      resultPosition?: number;
      accessedFrom?: AccessedFrom;
    }) {
      const {
        product,
        location,
        pageName,
        conversationId,
        searchId,
        resultPosition,
        accessedFrom,
      } = params;
      productInteraction({
        productId: product.id,
        actionType: 'added_to_cart',
        productName: product.name,
        location,
        orderId: order?.id,
        pageName,
        conversationId,
        searchId,
        resultPosition,
        accessedFrom,
      });

      const cart = onMutate(client, cartMutationState);
      if (cart !== null) {
        const nextCart = addOptimisticCartProduct(cart, product, {
          addVolume: true,
        });

        updateCart(client, nextCart);
      }

      return {
        previousCart: cart,
      };
    },
    onSettled(data) {
      onMutationSettled(
        client,
        cartMutationState,
        data,
        reconcileDesiredCounts
      );
    },
    onError(error, variables, context) {
      onMutationError(client, context);
      handleErrorWithToast(error);
    },
  });

  const addPairingMutation = useAddPairingSelf({
    onMutate(params) {
      const {
        pairing,
        ingredients,
        location,
        pageName,
        conversationId,
        searchId,
        resultPosition,
        accessedFrom,
      } = params;

      pairingInteraction({
        pairingId: pairing.id,
        pairingName: pairing.name,
        actionType: 'added_to_cart',
        location,
        orderId: order?.id,
        pageName,
        conversationId,
        searchId,
        resultPosition,
        accessedFrom,
      });

      const cart = onMutate(client, cartMutationState);
      if (cart !== null) {
        const nextCart = addPairingOptimisticCart(cart, pairing, ingredients, {
          addVolume: true,
        });
        updateCart(client, nextCart);
      }

      return {
        previousCart: cart,
      };
    },
    onSettled(data) {
      onMutationSettled(
        client,
        cartMutationState,
        data,
        reconcileDesiredCounts
      );
    },
    onError(error: unknown, variables, context) {
      onMutationError(client, context);
      handleErrorWithToast(error);
    },
  });
  const deleteCartBundleMutation = useDeleteBundleSelf({
    onMutate(params) {
      const {
        bundleId,
        pairingId,
        location,
        pageName,
        conversationId,
        searchId,
        resultPosition,
        accessedFrom,
      } = params;

      pairingInteraction({
        pairingId: params.pairingId,
        pairingName: params.pairingName,
        actionType: 'removed_from_cart',
        location,
        orderId: order?.id,
        pageName,
        conversationId,
        searchId,
        resultPosition,
        accessedFrom,
      });

      const cart = onMutate(client, cartMutationState);
      let deletedBundle: PairingBundle | null = null;
      if (cart !== null) {
        deletedBundle = getCartPairingBundle(cart, pairingId);

        const nextCart = produce(cart, (draft) => {
          draft.bundles = draft.bundles.filter((bundle) => {
            return bundle.id !== bundleId;
          });
        });

        updateCart(client, nextCart);
      }

      return {
        previousCart: cart,
        bundle: deletedBundle,
      };
    },
    onSettled(data) {
      onMutationSettled(
        client,
        cartMutationState,
        data,
        reconcileDesiredCounts
      );
    },
    onError(error, variables, context) {
      onMutationError(client, context);
      handleErrorWithToast(error);
    },
  });
  const deleteCartProductMutation = useDeleteCartProductSelf({
    onMutate(params) {
      const { bundleId, productId } = params;
      const cart = onMutate(client, cartMutationState);
      let deletedProduct: CartProduct | null = null;
      if (cart !== null) {
        const matchDeletedProduct = cart.products.find((product) => {
          return product.id === productId;
        });

        if (matchDeletedProduct !== undefined) {
          deletedProduct = matchDeletedProduct;
        }

        const nextCart = produce(cart, (draft) => {
          const bundle = draft.bundles.find((bundle) => {
            return bundle.id === bundleId;
          });

          if (bundle === undefined) {
            // Return if no bundle with matching ID is found.
            // With the change to the onSubmitUpdate function in the Cart,
            // we are now awaiting each successful request, so we want to
            // opt out of the optimistic update
            return;
          }

          const order_details: OrderDetail[] = [];
          let removed = false;
          bundle.order_details.forEach((orderDetail) => {
            const { product: orderDetailId } = orderDetail;
            if (orderDetailId === productId && removed === false) {
              removed = true;
              return;
            }
            order_details.push(orderDetail);
          });
          bundle.order_details = order_details;
        });

        updateCart(client, nextCart);
      }
      return {
        previousCart: cart,
        product: deletedProduct,
      };
    },
    onSettled(data) {
      onMutationSettled(
        client,
        cartMutationState,
        data,
        reconcileDesiredCounts
      );
    },
    onError(error, variables, context) {
      onMutationError(client, context);
      handleErrorWithToast(error);
    },
  });

  const patchCartBundleMutation = usePatchBundleSelf({
    onSuccess(nextCart) {
      updateCart(client, nextCart);
    },
    onError(error) {
      handleErrorWithToast(error);
      // A recipe edit sends one PATCH per modification, so a failure can land
      // with earlier modifications already applied. Nothing was written
      // optimistically to roll back — refetch so the cart shows what the
      // server actually kept.
      client.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.CART] });
    },
  });

  const addCartPairing: UseCartMutations['addCartPairing'] = (params) => {
    const {
      pairing,
      ingredients,
      modifications,
      location,
      pageName,
      conversationId,
      searchId,
      resultPosition,
      accessedFrom,
    } = params;

    const removeModifications = modifications?.filter(({ type }) => {
      return type === MutationTypes.RemoveIngredient;
    }) as RemoveIngredientMutation[];

    raiseDesiredCount('pairing', pairing.id);

    addPairingMutation.mutate(
      {
        pairing,
        ingredients,
        modifications: removeModifications,
        location,
        pageName,
        conversationId,
        searchId,
        resultPosition,
        accessedFrom,
      },
      {
        onSuccess(cart, _, context) {
          const bundle = getBundleDiffBetweenCarts(
            cart,
            context?.previousCart ?? null,
            pairing
          );
          executeChangeHandlers({
            type: CartChangeType.AddRecipe,
            bundle: bundle === undefined ? null : bundle,
            pairing: pairing,
          });
        },
      }
    );
  };

  /**
   * Sends the DELETE. Used by a customer's click and by the reconciler alike.
   * Returns false when the request was skipped as a duplicate.
   */
  function deleteRecipeBundle(removal: RecipeRemoval, bundleId: number) {
    if (inFlightBundleDeletes.has(bundleId)) return false;
    inFlightBundleDeletes.add(bundleId);
    deleteCartBundleMutation.mutate(
      {
        bundleId,
        pairingId: removal.recipeId,
        pairingName: removal.pairingName,
        location: removal.location,
        pageName: removal.pageName,
        conversationId: removal.conversationId,
        searchId: removal.searchId,
        resultPosition: removal.resultPosition,
        accessedFrom: removal.accessedFrom,
      },
      {
        onSuccess(data, params, context) {
          const bundle = context?.bundle ?? null;
          executeChangeHandlers({
            type: CartChangeType.RemoveRecipe,
            bundle,
          });
        },
        onSettled() {
          inFlightBundleDeletes.delete(bundleId);
        },
      }
    );
    return true;
  }

  const removeRecipeFromBundle: UseCartMutations['removeRecipeFromBundle'] = (
    params
  ) => {
    const { bundleId, ...removal } = params;
    const cart = getCartFromCache(client);

    // Record the count this click asked for before touching the cart, so the
    // reconciler can finish the job even if this particular delete can't run.
    const key = desiredCountKey('pairing', removal.recipeId);
    const previous = desiredCounts.get(key);
    const countedNow =
      cart === null ? 0 : getPairingCount(cart, removal.recipeId);
    desiredCounts.set(key, {
      kind: 'pairing',
      removal,
      attempts: 0,
      count: Math.max(0, (previous?.count ?? countedNow) - 1),
    });

    const targetBundleId =
      cart === null
        ? bundleId
        : resolvePairingBundleToRemove(cart, removal.recipeId, bundleId);
    if (targetBundleId === null) return;

    if (bundleIsOptimistic({ id: targetBundleId })) {
      // The add that created this bundle hasn't settled, so the server has no
      // id to DELETE yet. Show the decrement immediately and let the reconciler
      // send the real removal once the cart comes back.
      if (cart !== null) {
        const nextCart = produce(cart, (draft) => {
          draft.bundles = draft.bundles.filter((bundle) => {
            return bundle.id !== targetBundleId;
          });
        });
        updateCart(client, nextCart);
      }
      return;
    }

    deleteRecipeBundle(removal, targetBundleId);
  };

  /**
   * Closes the gap between what the customer asked for and what the server
   * holds, deleting the newest surplus first. Hoisted so the mutations above
   * can hand it to `onMutationSettled`; it only runs after a settle, by which
   * point the delete helpers exist.
   *
   * Runs after every cart refresh, so a target that can't be satisfied on this
   * pass is retried on the next one rather than discarded.
   */
  function reconcileDesiredCounts() {
    if (desiredCounts.size === 0) return;
    const cart = getCartFromCache(client);
    if (cart === null) return;

    desiredCounts.forEach((target, key) => {
      const settledBundleIds =
        target.kind === 'pairing'
          ? settledPairingBundleIds(cart, target.removal.recipeId)
          : settledProductBundleIds(cart, target.removal.productId);

      const surplus = settledBundleIds.length - target.count;
      if (surplus <= 0) {
        // The cart holds what was asked for; the intent is satisfied.
        desiredCounts.delete(key);
        return;
      }

      if (target.attempts >= MAX_RECONCILE_ATTEMPTS) {
        // Out of retries. Say so rather than letting the count quietly snap
        // back to a number the customer never asked for.
        desiredCounts.delete(key);
        handleErrorWithToast(
          new Error(`Unable to update the cart to ${target.count}`)
        );
        return;
      }

      // Take the ids up front: the delete mutations apply their cache updates
      // asynchronously, so re-reading between calls would reuse a bundle.
      let sent = 0;
      settledBundleIds.slice(0, surplus).forEach((bundleId) => {
        if (target.kind === 'product') {
          // Nothing awaits this, and the mutation rejects with the raw API
          // payload, so swallow it here or it surfaces as an unhandled
          // rejection. The mutation's own `onError` has already rolled the
          // cart back and toasted, so there's nothing further to do.
          deleteProductFromBundle(target.removal, bundleId).catch(() => {});
          sent += 1;
          return;
        }
        if (deleteRecipeBundle(target.removal, bundleId)) sent += 1;
      });

      // Only count a pass that actually reached the network, so waiting on a
      // duplicate delete can't burn through the retries.
      if (sent > 0) target.attempts += 1;
    });
  }

  /**
   * An add after a decrement is new intent: it raises the count the reconciler
   * is driving toward, so a pending removal can never eat the item the
   * customer just put back.
   */
  function raiseDesiredCount(kind: DesiredCount['kind'], itemId: number) {
    const target = desiredCounts.get(desiredCountKey(kind, itemId));
    if (target === undefined) return;
    target.count += 1;
  }

  const addCartProduct: UseCartMutations['addCartProduct'] = (params) => {
    const {
      product,
      location,
      pageName,
      conversationId,
      searchId,
      resultPosition,
      accessedFrom,
    } = params;

    raiseDesiredCount('product', product.id);

    addProductMutation.mutate(
      {
        product,
        location,
        pageName,
        conversationId,
        searchId,
        resultPosition,
        accessedFrom,
      },
      {
        onSuccess() {
          const category = product.categories?.[0]?.name;

          segmentAnalytics.track('Product Added - Web', {
            cart_id: order?.id,
            product_id: product.id,
            category,
          });
          executeChangeHandlers({
            type: CartChangeType.AddProduct,
            product,
          });
        },
      }
    );
  };

  /** Sends the DELETE. Used by a customer's click and by the reconciler alike. */
  async function deleteProductFromBundle(
    removal: ProductRemoval,
    bundleId: number
  ) {
    productInteraction({
      productId: removal.productId,
      actionType: 'removed_from_cart',
      productName: removal.productName ?? '',
      location: removal.location,
      orderId: order?.id,
      pageName: removal.pageName,
      conversationId: removal.conversationId,
      accessedFrom: removal.accessedFrom,
    });

    await deleteCartProductMutation.mutateAsync(
      {
        bundleId,
        productId: removal.productId,
        reasonId: removal.reasonId,
      },
      {
        onSuccess(data, params, context) {
          const product = context?.product ?? null;
          executeChangeHandlers({
            type: CartChangeType.RemoveProduct,
            product,
            isRecipeEdit: removal.isRecipeEdit,
          });
        },
      }
    );
  }

  const removeProductFromBundle: UseCartMutations['removeProductFromBundle'] =
    async (params) => {
      const { bundleId, ...removal } = params;
      const cart = getCartFromCache(client);
      const targetBundleId =
        cart === null
          ? bundleId
          : resolveProductBundleToRemove(cart, removal, bundleId);

      if (targetBundleId === null && removal.isRecipeEdit) {
        // The recipe this edit was opened on is gone, e.g. an unskip's shuffle
        // replaced it while the editor was open. Fail the edit like the 404
        // it would otherwise get, rather than silently dropping it, and leave
        // no target behind for the reconciler to act on.
        const error = new Error(
          `Bundle ${bundleId} for this recipe edit is no longer in the cart`
        );
        handleErrorWithToast(error);
        throw error;
      }

      // Record the count this click asked for before touching the cart, so the
      // reconciler can finish the job even if this delete can't run.
      const key = desiredCountKey('product', removal.productId);
      const previous = desiredCounts.get(key);
      const countedNow =
        cart === null ? 0 : getProductCount(cart, removal.productId);
      desiredCounts.set(key, {
        kind: 'product',
        removal,
        attempts: 0,
        count: Math.max(0, (previous?.count ?? countedNow) - 1),
      });

      if (targetBundleId === null) return;

      if (bundleIsOptimistic({ id: targetBundleId })) {
        // The add that created this bundle hasn't settled, so the server has
        // no id to DELETE yet. Show the decrement immediately and let the
        // reconciler send the real removal — mirrors the recipe path above.
        if (cart !== null) {
          const nextCart = produce(cart, (draft) => {
            draft.bundles = draft.bundles.filter((bundle) => {
              return bundle.id !== targetBundleId;
            });
          });
          updateCart(client, nextCart);
        }
        return;
      }

      await deleteProductFromBundle(removal, targetBundleId);
    };

  return {
    isMutating:
      addProductMutation.status === 'pending' ||
      addPairingMutation.status === 'pending' ||
      deleteCartBundleMutation.status === 'pending' ||
      deleteCartProductMutation.status === 'pending' ||
      patchCartBundleMutation.status === 'pending',
    isMutationSuccessful:
      addProductMutation.status === 'success' ||
      addPairingMutation.status === 'success' ||
      deleteCartBundleMutation.status === 'success' ||
      deleteCartProductMutation.status === 'success' ||
      patchCartBundleMutation.status === 'success',
    addCartPairing,
    removeRecipeFromBundle,
    removeProductFromBundle,
    addCartProduct,
    async swapRecipe(params) {
      const { bundleId, recipe } = params;
      const { id: recipeId } = recipe;

      await patchCartBundleMutation.mutateAsync({
        bundleId,
        pairingId: recipeId,
      });
    },

    async addProductsToBundle(params) {
      const { bundleId, productIds } = params;

      for (const productId of productIds) {
        productInteraction({
          productId,
          actionType: 'added_to_cart',
          location: 'rdp',
          orderId: order?.id,
        });

        await patchCartBundleMutation.mutateAsync({
          bundleId,
          productId,
        });
      }
    },

    // This function handles adding pairings for places *OUTSIDE* the cart
    addPairing({
      pairing,
      location,
      pageName,
      conversationId,
      searchId,
      resultPosition,
      accessedFrom,
    }) {
      const ingredientsMap = pairing.ingredients.reduce(
        (seed, ingredient) => {
          seed[ingredient.id] = ingredient;
          return seed;
        },
        {} as Record<string, OrderIngredient>
      );
      const cartPairing = convertPairingToCartPairing(pairing);
      const ingredients = cartPairing.ingredients.map(
        ({ id: ingredientId }) => {
          const ingredient = ingredientsMap[ingredientId];
          return convertOrderIngredientToCartProduct(ingredient);
        }
      );

      addCartPairing({
        pairing: cartPairing,
        ingredients,
        location,
        pageName,
        conversationId,
        searchId,
        resultPosition,
        accessedFrom,
      });
    },

    // This function handles removing PAIRINGS for places *OUTSIDE* the cart
    removePairing({
      pairing,
      onSuccess,
      location,
      pageName,
      conversationId,
      searchId,
      resultPosition,
      accessedFrom,
    }) {
      const { id: pairingId } = pairing;
      const cart = getCartFromCache(client);
      // The recipe can be gone by the time a deferred removal runs, e.g. the
      // cart was re-shuffled after an unskip.
      if (cart === null || getPairingCount(cart, pairingId) === 0) {
        return;
      }
      const bundle = getLatestCartPairingBundle(cart, pairingId);
      const { id: bundleId } = bundle;
      removeRecipeFromBundle({
        bundleId,
        recipeId: pairingId,
        pairingName: pairing.name,
        location,
        pageName,
        conversationId,
        searchId,
        resultPosition,
        accessedFrom,
      });
      onSuccess?.();
    },

    // This function handles adding products for places *OUTSIDE* the cart
    addProduct({
      product,
      location,
      pageName,
      conversationId,
      searchId,
      resultPosition,
      accessedFrom,
    }) {
      addCartProduct({
        product: convertProductToCartProduct(product),
        location,
        pageName,
        conversationId,
        searchId,
        resultPosition,
        accessedFrom,
      });
    },

    // This function handles removing products for places *OUTSIDE* the cart
    removeProduct({
      product,
      onSuccess,
      location,
      pageName,
      conversationId,
      accessedFrom,
    }) {
      const { id: productId } = product;
      const cart = getCartFromCache(client);
      if (cart === null) {
        return;
      }
      const productBundle = getCartProductBundle(cart, productId);
      if (productBundle !== undefined) {
        const { id: productBundleId } = productBundle;
        removeProductFromBundle({
          bundleId: productBundleId,
          productId: productId,
          productName: product.name,
          location,
          pageName,
          conversationId,
          accessedFrom,
        });
        onSuccess?.();
      }
    },
  };
}
